import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Eye } from 'lucide-react';

const API_URL = window.__BACKEND_URL;

export default function HelpArticle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [id]);

  const loadArticle = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/support/help/articles/${id}`);
      setArticle(response.data);
    } catch (err) {
      console.error('Error loading article:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 text-lg mb-4">Article not found</p>
          <button
            onClick={() => navigate('/help')}
            className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:opacity-90"
          >
            Back to Help Center
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <div className="bg-black/20 backdrop-blur-md border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <button
            onClick={() => navigate('/help')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Help Center
          </button>
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <article className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-8">
          <h1 className="text-4xl font-bold text-white mb-6">{article.title}</h1>
          
          <div className="flex items-center gap-6 text-sm text-gray-400 mb-8 pb-6 border-b border-white/10">
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {new Date(article.created_at).toLocaleDateString()}
            </span>
            <span className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              {article.view_count} views
            </span>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="text-gray-300 text-lg leading-relaxed whitespace-pre-wrap">
              {article.body}
            </div>
          </div>
        </article>

        {/* Was this helpful? */}
        <div className="mt-8 p-6 bg-white/5 backdrop-blur-md border border-white/10 rounded-lg text-center">
          <p className="text-white mb-4">Was this article helpful?</p>
          <div className="flex gap-4 justify-center">
            <button className="px-6 py-2 bg-green-500/20 border border-green-500/30 text-green-300 rounded-lg hover:bg-green-500/30 transition-colors">
              👍 Yes
            </button>
            <button className="px-6 py-2 bg-red-500/20 border border-red-500/30 text-red-300 rounded-lg hover:bg-red-500/30 transition-colors">
              👎 No
            </button>
          </div>
        </div>

        {/* Contact Support */}
        <div className="mt-8 p-6 bg-cyan-500/10 border border-cyan-500/20 rounded-lg text-center">
          <p className="text-gray-300 mb-4">Still need help with this topic?</p>
          <button
            onClick={() => navigate('/contact-support')}
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
}
